import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { RootState } from "../../state/store";
import { claimWhatsNewView, WHATS_NEW_VIDEO_ID } from "./storage";

const EMBED_URL = `https://www.youtube.com/embed/${WHATS_NEW_VIDEO_ID}?rel=0&modestbranding=1`;

const WhatsNewModal = () => {
  const userId = useSelector(
    (state: RootState) => state.company.company_details?.user_id
  );
  const showCompanySwitcher = useSelector(
    (state: RootState) => state.auth.showCompanySwitcher
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId || showCompanySwitcher) {
      return;
    }
    if (claimWhatsNewView(userId)) {
      setOpen(true);
    }
  }, [userId, showCompanySwitcher]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <Overlay onMouseDown={() => setOpen(false)}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <CloseButton type="button" aria-label="Close" onClick={() => setOpen(false)}>
          <FiX size={18} />
        </CloseButton>
        <Eyebrow>What&apos;s new in</Eyebrow>
        <Title id="whats-new-title">Sapaad Vantage?</Title>
        <VideoFrame>
          <iframe
            src={EMBED_URL}
            title="What's new in Sapaad Vantage?"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </VideoFrame>
      </Dialog>
    </Overlay>
  );
};

export default WhatsNewModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(33, 33, 33, 0.55);
`;

const Dialog = styled.div`
  position: relative;
  width: min(560px, 100%);
  padding: 22px 22px 20px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #d0d0d0;
  border-radius: 50%;
  background: #ffffff;
  color: #6b6b6b;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
    color: #212121;
  }
`;

const Eyebrow = styled.p`
  margin: 0 36px 2px 0;
  color: #8a8a8a;
  font-size: 14px;
  line-height: 1.3;
`;

const Title = styled.h2`
  margin: 0 36px 16px 0;
  color: #1a1a1a;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  border-radius: 8px;
  background: #000000;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
